import { TriangleDownIcon, XCircleFillIcon } from '@primer/octicons-react';
import { Box, Button, SelectPanel } from '@primer/react';
import { forwardRef, useMemo, useState } from 'react';
import { getField, getItems, removeVietnameseTones } from './common';

export interface SelectBoxProps {
    dataSource?: any;
    items?: any;
    displayExpr?: string | ((x: any) => string);
    valueExpr?: string;
    value?: any;
    placeholder?: string;
    readOnly?: boolean;
    disabled?: boolean;
    showClearButton?: boolean;
    width?: any;
    height?: any;
    onValueChanged?: (e: { value: any; event?: any; previousValue?: any }) => void;
    [key: string]: any;
}

/** Dropdown chọn 1 giá trị (Primer SelectPanel), API tương thích SelectBox cũ: dataSource/valueExpr/displayExpr/onValueChanged */
const SelectBox = forwardRef<any, SelectBoxProps>((props, _ref) => {
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState('');
    const { valueExpr, displayExpr } = props;
    const raw = getItems(props);

    const options = useMemo(
        () =>
            raw.map((x: any, idx: number) => {
                const v = getField(x, valueExpr);
                return { id: v ?? idx, text: String(getField(x, displayExpr) ?? ''), value: v };
            }),
        [raw, valueExpr, displayExpr]
    );
    const filtered = useMemo(() => {
        const f = removeVietnameseTones(filter.toLowerCase());
        return options.filter((o) => removeVietnameseTones(o.text.toLowerCase()).includes(f));
    }, [options, filter]);
    const selected = useMemo(
        () => options.find((o) => o.value === props.value && props.value !== null && props.value !== undefined),
        [options, props.value]
    );
    const isDisabled = props.readOnly || props.disabled;

    const emit = (value: any) => props.onValueChanged?.({ value, event: {}, previousValue: props.value });

    const searchPlaceholder = useMemo(() => {
        if (!props.placeholder) return 'Tìm kiếm...';
        const clean = props.placeholder.replace(/^(chọn|select)\s+/i, '');
        return `Tìm kiếm ${clean.toLowerCase()}...`;
    }, [props.placeholder]);

    const canClear = (props.showClearButton ?? true) && selected && !isDisabled;

    return (
        <Box sx={{ width: props.width ? props.width : 'fit-content', display: props.width ? 'block' : 'inline-block', maxWidth: '100%' }}>
            <SelectPanel
                renderAnchor={({ 'aria-labelledby': ariaLabelledBy, ...anchorProps }: any) => (
                    <Button
                        className="selectbox-anchor"
                        sx={{
                            width: props.width ? '100%' : 'auto',
                            minWidth: 0,
                            maxWidth: '100%',
                            display: props.width ? 'flex' : 'inline-flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            textAlign: 'left',
                            '& > [data-component="buttonContent"]': {
                                flex: '1 1 auto',
                                justifyContent: 'flex-start',
                                textAlign: 'left',
                                overflow: 'hidden',
                                minWidth: 0,
                            },
                            '& [data-component="text"]': {
                                textAlign: 'left',
                                display: 'block',
                                overflow: 'hidden',
                            },
                            '& [data-component="trailingAction"]': {
                                ml: 2,
                                flexShrink: 0,
                            },
                        }}
                        trailingAction={TriangleDownIcon}
                        disabled={isDisabled}
                        aria-labelledby={ariaLabelledBy}
                        {...anchorProps}
                    >
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 400, display: 'block', textAlign: 'left', whiteSpace: 'nowrap' }}>
                            {selected ? selected.text : props.placeholder ?? 'Chọn'}
                        </span>
                    </Button>
                )}
                title={
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Box sx={{ flex: 1, fontWeight: 600 }}>{props.placeholder ?? 'Chọn'}</Box>
                        {canClear && (
                            <Button
                                trailingVisual={XCircleFillIcon}
                                variant="invisible"
                                size="small"
                                sx={{
                                    color: 'danger.fg',
                                    p: 0,
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    '&:hover': { color: 'danger.emphasis', textDecoration: 'none' },
                                }}
                                onClick={(e: any) => {
                                    e.stopPropagation();
                                    emit(null);
                                    setOpen(false);
                                }}
                            >
                                Bỏ chọn
                            </Button>
                        )}
                    </Box>
                }
                placeholderText={searchPlaceholder}
                open={open}
                onOpenChange={setOpen}
                items={filtered}
                selected={selected as any}
                onSelectedChange={(item: any) => {
                    emit(item ? item.value : null);
                    setOpen(false);
                }}
                onFilterChange={setFilter}
                showItemDividers
                overlayProps={{ width: 'medium', height: 'medium' }}
            />
        </Box>
    );
});
export default SelectBox;